import { Route, Router, Switch, useLocation } from 'wouter-preact'
import { useHashLocation } from 'wouter-preact/use-hash-location'
import { UpdateNotice } from '~/components/feedback/UpdateNotice'
import { TabBar } from '~/components/layout/TabBar'
import { NextPhasePage } from '~/pages/nextPhase/NextPhasePage'
import { PhotosPage } from '~/pages/photos/PhotosPage'
import { PlanPage } from '~/pages/plan/PlanPage'
import { ProgressPage } from '~/pages/progress/ProgressPage'
import { ReviewPage } from '~/pages/review/ReviewPage'
import { SettingsPage } from '~/pages/settings/SettingsPage'
import { ProjectSetupPage } from '~/pages/setup/ProjectSetupPage'
import { TodayPage } from '~/pages/today/TodayPage'
import {
  DEFAULT_ROUTE,
  NEXT_PHASE_ROUTE,
  ONBOARDING_ROUTE,
  PLAN_FROM_PROPOSAL_ROUTE,
  PLAN_ROUTE,
} from '~/routes'
import { DatabaseProvider } from '~/storage/DatabaseContext'
import type { SolaDatabase } from '~/storage/db'

/**
 * Application shell.
 *
 * Hash routing is deliberate (D-025): every route resolves to the same
 * document, so the app deploys to any static host without rewrite rules and the
 * service worker's navigation handling stays trivial.
 */
interface AppProps {
  /** Optional so the shell can be rendered in tests without a database. */
  db?: SolaDatabase
}

export function App({ db }: AppProps) {
  return (
    <DatabaseProvider value={db}>
      <Router hook={useHashLocation}>
        <a class="skip-link" href="#main-content">
          Skip to main content
        </a>
        <Routes />
      </Router>
    </DatabaseProvider>
  )
}

function Routes() {
  const [location] = useLocation()

  /* Onboarding is a focused, full-screen task. Showing tabs during it would
     invite the user to wander off mid-setup into screens that cannot work yet.
     The next-phase flow is suppressed for the same reason: it re-takes a
     measurement, and coming back to a half-entered set of readings is the same
     hazard (D-103 §3). Leaving either is a labelled control, not an accident. */
  const isFocusedTask = location === ONBOARDING_ROUTE || location === NEXT_PHASE_ROUTE

  return (
    <>
      <Switch>
        <Route path={ONBOARDING_ROUTE} component={ProjectSetupPage} />
        <Route path={NEXT_PHASE_ROUTE} component={NextPhasePage} />
        <Route path="/today" component={TodayPage} />
        <Route path="/progress" component={ProgressPage} />
        <Route path="/review" component={ReviewPage} />
        <Route path="/photos" component={PhotosPage} />
        <Route path="/settings" component={SettingsPage} />
        {/* Before the bare plan route: wouter matches in order, and `/plan`
            would otherwise not match the longer path anyway — kept adjacent so
            the two are read together. */}
        <Route path={PLAN_FROM_PROPOSAL_ROUTE} component={PlanPage} />
        <Route path={PLAN_ROUTE} component={PlanPage} />
        {/* Any unknown hash, including the empty initial one, lands on Today. */}
        <Route>
          <RedirectToDefault />
        </Route>
      </Switch>

      {/* First-run setup is a focused one-time task; an update announcement
          during it would compete with the only thing the user can usefully do.
          The update still waits, and is announced as soon as setup finishes. */}
      {isFocusedTask ? null : <UpdateNotice />}
      {isFocusedTask ? null : <TabBar />}
    </>
  )
}

function RedirectToDefault() {
  // Replace rather than push, so the unknown route does not become a back-stop.
  if (typeof window !== 'undefined') {
    window.location.replace(`#${DEFAULT_ROUTE}`)
  }
  return null
}
